import { Injectable, inject } from '@angular/core';
import { ToasterService } from '../../../services/toaster.service';
import { PrintChainFailedPayload, buildChainFailureMessage } from './print-monitor';

/**
 * App-wide print-failure monitor (R45).
 *
 * The Tauri shell emits `print-chain-failed` when EVERY configured method of
 * a dispatch failed or fell through. Until R45 the only failure listener was
 * the Printing Options page's `print-result` feed, so a print fired from any
 * other screen (e.g. Pooja Enquiry v2's Print Preview) could end with zero
 * output and zero feedback. This monitor is initialised once from
 * AppComponent and turns every exhausted chain into a visible error toast,
 * whatever screen the operator is on.
 *
 * No-op outside the desktop build.
 */
@Injectable({ providedIn: 'root' })
export class PrintFailureMonitorService {

    private readonly toaster = inject(ToasterService);
    private started = false;
    private unlisten: (() => void) | null = null;

    /** Idempotent; safe to call from AppComponent on every build. */
    async init(): Promise<void> {
        if (this.started) {
            return;
        }
        this.started = true;
        if (typeof (window as any).__TAURI__ === 'undefined') {
            return;
        }
        try {
            const { listen } = await import('@tauri-apps/api/event');
            this.unlisten = await listen<PrintChainFailedPayload>(
                'print-chain-failed',
                event => this.handleChainFailed(event.payload)
            );
        } catch (e) {
            console.warn('print-chain-failed listener unavailable', e);
        }
    }

    /** Exposed for specs — composes and shows the operator-facing toast. */
    handleChainFailed(payload: PrintChainFailedPayload): void {
        this.toaster.error(buildChainFailureMessage(payload), 'PRINT FAILED', 'topRight', 12000);
    }

    destroy(): void {
        if (this.unlisten) {
            this.unlisten();
            this.unlisten = null;
        }
        this.started = false;
    }
}
